import { useState, useEffect, useCallback, useMemo } from "react";
import TourDataService from "../services/tourDataService";

/**
 * Hook optimizado para manejar los datos del tour
 * Incluye memoización y optimizaciones de performance
 */
export const useTourData = (tourId, mode = "create", mappedTourData = null) => {
  // Estados de datos
  const [tourData, setTourData] = useState(null);
  const [models, setModels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [gpsDevices, setGpsDevices] = useState([]);
  const [tourPrices, setTourPrices] = useState({});
  const [gpsPrices, setGpsPrices] = useState({});
  const [insurancePrices, setInsurancePrices] = useState({});
  const [clientRewards, setClientRewards] = useState({});
  const [priceDate, setPriceDate] = useState(new Date().toISOString().split('T')[0]);
  const [holidays, setHolidays] = useState([]);
  
  // Estados de control
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoizar el tourId para evitar re-fetches innecesarios
  const memoizedTourId = useMemo(() => tourId, [tourId]);
  const memoizedMode = useMemo(() => mode, [mode]);

  /**
   * Función optimizada para cargar los datos del tour
   */
  const fetchTourData = useCallback(async () => {
    if (!memoizedTourId) return;

    console.log('Starting to fetch tour data for ID:', memoizedTourId, 'Mode:', memoizedMode)
    setLoading(true);
    setError(null);

    try {
      // Cargar datos principales en paralelo
      const [
        tourDataResult,
        modelsResult,
        locationsResult
      ] = await Promise.all([
        mode === "create" 
          ? TourDataService.fetchPendingTourData(memoizedTourId)
          : TourDataService.fetchConfirmedTourData(memoizedTourId),
        TourDataService.fetchVehicleModels(mode === "edit" ? "2_2" : "2"),
        TourDataService.fetchLocations()
      ]);

      console.log('Tour data result:', tourDataResult)
      console.log('Models result:', modelsResult)
      console.log('Locations result:', locationsResult)

      setTourData(tourDataResult);
      setModels(modelsResult || []);
      setLocations(locationsResult || []);

      // Extraer fecha de precios del tour
      const tourPriceDate = (tourDataResult.booking && tourDataResult.booking.price_date) || 
                           (tourDataResult.booking && tourDataResult.booking.tour && tourDataResult.booking.tour.price_date) ||
                           new Date().toISOString().split('T')[0];
      setPriceDate(tourPriceDate);

      // Cargar precios en paralelo
      const [
        tourPricesResult,
        gpsPricesResult,
        insurancePricesResult,
        clientRewardsResult
      ] = await Promise.all([
        TourDataService.fetchTourPrices(
          (tourDataResult.booking && tourDataResult.booking.tour_id) || (tourDataResult.booking && tourDataResult.booking.tour_id),
          tourPriceDate
        ),
        TourDataService.fetchGpsPrices(tourPriceDate),
        TourDataService.fetchInsurancePrices(tourPriceDate),
        TourDataService.fetchClientRewards(tourDataResult.driver && tourDataResult.driver.id)
      ]);

      setTourPrices(tourPricesResult);
      setGpsPrices(gpsPricesResult);
      setInsurancePrices(insurancePricesResult);
      setClientRewards(clientRewardsResult);

      // Cargar vehículos y GPS solo si hay modelo seleccionado (como en el componente original)
      console.log('Tour data result:', tourDataResult);
      console.log('Booking info:', tourDataResult.booking);
      
      if (tourDataResult.booking && tourDataResult.booking.vehicle_model_1) {
        const tourIdForVehicles = tourDataResult.booking.tour_id;
        const modelIdForVehicles = tourDataResult.booking.vehicle_model_1;
        
        console.log('Tour ID for vehicles:', tourIdForVehicles);
        console.log('Model ID for vehicles:', modelIdForVehicles);
        
        const [vehiclesResult, gpsDevicesResult] = await Promise.all([
          TourDataService.fetchAvailableVehicles(tourIdForVehicles, modelIdForVehicles),
          TourDataService.fetchGpsDevices(tourIdForVehicles)
        ]);

        console.log('Vehicles result:', vehiclesResult);
        console.log('GPS devices result:', gpsDevicesResult);

        setVehicles(vehiclesResult || []);
        setGpsDevices(gpsDevicesResult || []);
      } else {
        console.log('No vehicle model selected, setting empty arrays');
        setVehicles([]);
        setGpsDevices([]);
      }

      // Cargar días festivos si hay ubicaciones
      if (tourDataResult.location_id_1 && tourDataResult.date_1 && tourDataResult.date_2) {
        try {
          const holidaysResult = await TourDataService.fetchHolidays(
            tourDataResult.location_id_1,
            tourDataResult.date_1,
            tourDataResult.date_2
          );
          setHolidays(holidaysResult);
        } catch (holidayError) {
          console.warn('Error loading holidays:', holidayError);
          setHolidays([]);
        }
      }

    } catch (err) {
      console.error("Error fetching tour data:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [memoizedTourId, memoizedMode]);

  /**
   * Función optimizada para obtener vehículos de un modelo específico
   */
  const fetchVehicles = useCallback(async (modelId) => {
    if (!memoizedTourId || !modelId) return;

    try {
      const vehiclesResult = await TourDataService.fetchAvailableVehicles(
        (tourData && tourData.booking && tourData.booking.tour_id) || (tourData && tourData.booking && tourData.booking.tour_id),
        modelId
      );
      setVehicles(vehiclesResult);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
      setVehicles([]);
    }
  }, [memoizedTourId, tourData]);

  /**
   * Función para actualizar vehículos y GPS cuando cambia el modelo (similar a handelModelChange)
   */
  const updateVehiclesAndGpsForModel = useCallback(async (modelId) => {
    if (!tourData || !tourData.booking || !tourData.booking.tour_id) return;

    try {
      console.log('Updating vehicles and GPS for model:', modelId);
      
      const [vehiclesResult, gpsDevicesResult] = await Promise.all([
        TourDataService.fetchAvailableVehicles(tourData.booking.tour_id, modelId),
        TourDataService.fetchGpsDevices(tourData.booking.tour_id)
      ]);

      console.log('Updated vehicles result:', vehiclesResult);
      console.log('Updated GPS devices result:', gpsDevicesResult);

      setVehicles(vehiclesResult || []);
      setGpsDevices(gpsDevicesResult || []);
    } catch (err) {
      console.error("Error updating vehicles and GPS:", err);
      setVehicles([]);
      setGpsDevices([]);
    }
  }, [tourData]);

  /**
   * Función optimizada para recargar todos los datos
   */
  const refetch = useCallback(() => {
    fetchTourData();
  }, [fetchTourData]);

  /**
   * Función optimizada para actualizar precios
   */
  const updatePrices = useCallback(async (newPriceDate) => {
    if (!memoizedTourId || !newPriceDate) return;

    try {
      const [
        tourPricesResult,
        gpsPricesResult,
        insurancePricesResult
      ] = await Promise.all([
        TourDataService.fetchTourPrices(
          (tourData && tourData.booking && tourData.booking.tour_id) || (tourData && tourData.booking && tourData.booking.tour_id),
          newPriceDate
        ),
        TourDataService.fetchGpsPrices(newPriceDate),
        TourDataService.fetchInsurancePrices(newPriceDate)
      ]);

      setTourPrices(tourPricesResult);
      setGpsPrices(gpsPricesResult);
      setInsurancePrices(insurancePricesResult);
      setPriceDate(newPriceDate);
    } catch (err) {
      console.error("Error updating prices:", err);
    }
  }, [memoizedTourId, tourData]);

  // Funciones de utilidad memoizadas
  const getModelName = useCallback((modelId) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.model_name : "Modelo no encontrado";
  }, [models]);

  const getVehicleName = useCallback((vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.name : "Vehículo no encontrado";
  }, [vehicles]);

  const getGpsDeviceName = useCallback((gpsDeviceId) => {
    const gpsDevice = gpsDevices.find(g => g.id === gpsDeviceId);
    return gpsDevice ? gpsDevice.name : "GPS no encontrado";
  }, [gpsDevices]);

  const isVehicleAvailable = useCallback((vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.availability : false;
  }, [vehicles]);

  const isGpsDeviceAvailable = useCallback((gpsDeviceId) => {
    const gpsDevice = gpsDevices.find(g => g.id === gpsDeviceId);
    return gpsDevice ? gpsDevice.availability : false;
  }, [gpsDevices]);

  // Valores derivados memoizados
  const selectedModel = useMemo(() => {
    if (!(tourData && tourData.booking && tourData.booking.vehicle_model_1)) return null;
    return models.find(m => m.id === tourData.booking.vehicle_model_1);
  }, [tourData, models]);

  const selectedVehicle = useMemo(() => {
    if (!(tourData && tourData.vehicle_id)) return null;
    return vehicles.find(v => v.id === tourData.vehicle_id);
  }, [tourData, vehicles]);

  const selectedGpsDevice = useMemo(() => {
    if (!(tourData && tourData.gpsdevice_id)) return null;
    return gpsDevices.find(g => g.id === tourData.gpsdevice_id);
  }, [tourData, gpsDevices]);

  const availableVehicles = useMemo(() => {
    return vehicles.filter(v => v.availability);
  }, [vehicles]);

  const availableGpsDevices = useMemo(() => {
    return gpsDevices.filter(g => g.availability);
  }, [gpsDevices]);

  // Efecto para cargar datos cuando cambia el tourId o modo
  useEffect(() => {
    // Si tenemos datos mapeados, usarlos directamente
    if (mappedTourData) {
      console.log('Using mapped tour data:', mappedTourData);
      setTourData(mappedTourData);
      setLoading(false);
      setError(null);
      return;
    }
    
    // Si no hay datos mapeados, cargar desde el servicio
    fetchTourData();
  }, [fetchTourData, mappedTourData]);

  return {
    // Datos principales
    tourData,
    models,
    vehicles,
    gpsDevices,
    tourPrices,
    gpsPrices,
    insurancePrices,
    clientRewards,
    locations,
    holidays,
    priceDate,
    
    // Estados de control
    loading,
    error,
    
    // Funciones
    fetchVehicles,
    refetch,
    updatePrices,
    updateVehiclesAndGpsForModel,
    
    // Funciones de utilidad
    getModelName,
    getVehicleName,
    getGpsDeviceName,
    isVehicleAvailable,
    isGpsDeviceAvailable,
    
    // Valores derivados
    selectedModel,
    selectedVehicle,
    selectedGpsDevice,
    availableVehicles,
    availableGpsDevices
  };
}; 


// WEBPACK FOOTER //
// ./src/hooks/useTourData.js